import { Box, Typography, useTheme, Grid, Card, CardContent, IconButton, LinearProgress, Chip, CircularProgress, Button, Skeleton, Tooltip as MuiTooltip, Divider, Menu, MenuItem, ListItemIcon, ListItemText, Stack } from "@mui/material";
import { tokens } from "../../theme";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
// Overview-only icons — for Federation Health strip, Quick Actions, Pipeline Snapshot
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import SecurityIcon from "@mui/icons-material/Security";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DnsIcon from "@mui/icons-material/Dns";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PieChartIcon from "@mui/icons-material/PieChart";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { memo, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../contexts/AppContext";
import { MODEL_COUNT } from "../../data/modelCatalog";
import { groupCapabilitiesByDatasetRef } from "../../data/mockSites";

import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, Filler } from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { formatDateToLocal, formatRelativeTime } from '../../utils/dateUtils';
import RecentActivitiesModal from './components/RecentActivitiesModal';
import AllCompletedRunsModal from '../../modals/AllCompletedRunsModal';
import { resolveDatasetDisplayNameForExecution } from '../../utils/datasetDisplay';
import { buildConfigDataFromActivity } from '../../utils/restoreRunConfig';
import HeaderWithControls from "../../components/HeaderWithControls";
import { ActivityCalendar  } from "react-activity-calendar";
import { STATUS_COLORS } from "../../constants";
import ClearHistoryModal from "./components/ClearHistoryModal";
import DashboardBanner from "./components/DashboardBanner";
import SitesMapPanel from "./components/SitesMapPanel";

// ── Overview stat icons (custom SVGs) ─────────────────────────────────
// Sized with 1em so they inherit the `& svg { fontSize }` set by their
// container, and use currentColor so they pick up each card's accent.
const svgBase = {
  width: "1em",
  height: "1em",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  xmlns: "http://www.w3.org/2000/svg",
};

// DataHub Datasets — stacked data store
const DataHubDatasetsIcon = (props) => (
  <svg {...svgBase} {...props}>
    <ellipse cx="12" cy="5" rx="7" ry="2.6" />
    <path d="M5 5v6c0 1.45 3.13 2.6 7 2.6s7-1.15 7-2.6V5" />
    <path d="M5 11v6c0 1.45 3.13 2.6 7 2.6s7-1.15 7-2.6v-6" />
  </svg>
);

// Learning Models — neural network nodes
const LearningModelsIcon = (props) => (
  <svg {...svgBase} {...props}>
    <circle cx="5" cy="6" r="2" />
    <circle cx="5" cy="18" r="2" />
    <circle cx="12" cy="12" r="2.3" />
    <circle cx="19" cy="6" r="2" />
    <circle cx="19" cy="18" r="2" />
    <path d="M6.9 6.9 9.9 11M6.9 17.1 9.9 13M14.1 11 17.1 6.9M14.1 13 17.1 17.1" />
  </svg>
);

// Incomplete Rounds — partially-filled progress ring
const IncompleteRoundsIcon = (props) => (
  <svg {...svgBase} {...props}>
    <circle cx="12" cy="12" r="9" opacity="0.3" />
    <path d="M12 3a9 9 0 0 1 8.49 6" />
    <path d="M12 7.5V12l3 1.8" opacity="0.9" />
  </svg>
);

// Completed Rounds — full ring with check
const CompletedRoundsIcon = (props) => (
  <svg {...svgBase} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.3l2.6 2.6L16 9.4" />
  </svg>
);

// Training Completed — award medal with check
const TrainingCompletedIcon = (props) => (
  <svg {...svgBase} {...props}>
    <circle cx="12" cy="9" r="6" />
    <path d="M9.6 9l1.7 1.7L14.7 7.4" />
    <path d="M8.4 13.9 7 21l5-2.4L17 21l-1.4-7.1" />
  </svg>
);

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

// Unwrap default if it's a module object (Vite/Rollup interop)


// Safety check to prevent white screen of death
// We only render this valid, extracted component.
// If it's still an object that React can't render (and not a memo/forwardRef), it will crash inside React,
// but usually the unwrap handles the "Namespace Object" case.

import { useTranslation } from "react-i18next";

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

// Render counter to track re-renders
let renderCount = 0;

const Dashboard = () => {
  renderCount++;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  
  // Responsive Calendar Logic
  const containerRef = useRef(null);
  const [blockSize, setBlockSize] = useState(12);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      // 53 weeks + approx 40px for labels/padding
      // Calculate max block size that fits
      const calculatedSize = Math.floor((width - 10) / 40);
      const clampedSize = Math.max(6, Math.min(14, calculatedSize));
      setBlockSize(clampedSize);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const navigate = useNavigate();
  
  // State for the activities modal
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  
  // State for the actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // State for the all completed runs modal
  const [showAllCompletedRuns, setShowAllCompletedRuns] = useState(false);
  // Full activity object passed to modal so it can render details even when
  // the list lookup misses (e.g. mock data, runs without trainingRunId).
  const [initialSelectedActivity, setInitialSelectedActivity] = useState(null);
  const [isTrainingActive, setIsTrainingActive] = useState(false);
  const [openClearHistoryModal, setOpenClearHistoryModal] = useState(false);
  
  // Get AppContext but prevent re-renders during training
  const appContext = useAppContext();
  const frozenContextRef = useRef(null);
  const [isTrainingActiveState, setIsTrainingActiveState] = useState(false);

    // Use frozen context during training, live context otherwise
    const activeContext = frozenContextRef.current || appContext;
  
    // Extract values from active context
    const {
      models,
      datasets,
      // Pulled in so Dashboard stats reflect the same /datasets logical-group
      // count and the /sites composition (4 hospitals + 2 research institutions).
      availableDatasets,
      // Resolver needs siteCapabilities to translate saved
      // config_data.dataset_config.sites[].capability_id back to a
      // dataset_ref (and thence to mockDatasetDefinitions display_name) —
      // same path the Configuration Details Dataset row uses.
      siteCapabilities,
      sites,
      trainingStatus,
      trainingSummary,
      executionHistory,
      fetchTrainingSummary,
      refreshTrainingStatus,
      refreshTrainingProgress,
      saveConfig,
      modelsLoading,
      datasetsLoading,
      statusLoading,
      configLoading,
      trainingProgress,
      isRunning,
      isPaused,
      currentConfig,
      setCurrentConfig,
      clearAllTrainingData,
      executionHistoryLoading
    } = activeContext;
  
  // Training status will be determined later with memoization
  
  // Single effect to handle training state changes and data refresh
  useEffect(() => {
    const currentlyTraining = appContext.trainingStatus?.status === 'running' || 
                             appContext.trainingStatus?.status === 'paused' || 
                             appContext.isRunning || 
                             appContext.isPaused;
    
    // Handle training state transitions
    if (currentlyTraining && !isTrainingActiveState) {
      // Training just started - freeze current context with stable loading states
      console.log('[Dashboard] 🟡 FREEZING AppContext - Training started');
      frozenContextRef.current = { 
        ...appContext,
        // Ensure loading states are false when frozen to prevent flicker
        modelsLoading: false,
        datasetsLoading: false,
        statusLoading: false,
        configLoading: false
      };
      setIsTrainingActiveState(true);
      setLastTrainingState(true);
    } else if (!currentlyTraining && isTrainingActiveState) {
      // Training just stopped - unfreeze context and refresh data
      console.log('[Dashboard] 🟢 UNFREEZING AppContext - Training completed');
      frozenContextRef.current = null;
      setIsTrainingActiveState(false);
      setLastTrainingState(false);
      setHasCompletedTraining(true);
      
      // Refresh data once when training completes
      if (fetchTrainingSummary) {

        fetchTrainingSummary().then(() => {
          setHasCompletedTraining(false); // Reset flag after refresh
        }).catch((error) => {
          console.error('[Dashboard] ❌ Error refreshing training summary:', error);
          setHasCompletedTraining(false); // Reset flag even on error
        });
      }
      
      // Also refresh training status
      if (refreshTrainingStatus) {
        refreshTrainingStatus();
      }
    }
  }, [appContext.trainingStatus?.status, appContext.isRunning, appContext.isPaused, isTrainingActiveState, fetchTrainingSummary, refreshTrainingStatus]);
  

  
  // Add missing trainingDataLoading state
  const [trainingDataLoading, setTrainingDataLoading] = useState(false);

  // Simplified training state management to prevent infinite re-renders
  const [lastTrainingState, setLastTrainingState] = useState(false);
  const [hasCompletedTraining, setHasCompletedTraining] = useState(false);
  
  // Stable training detection
  const isCurrentlyTraining = useMemo(() => {
    const result = trainingStatus?.status === 'running' || trainingStatus?.status === 'paused' || isRunning || isPaused;

    return result;
  }, [trainingStatus?.status, isRunning, isPaused]);

  // Use frozen data during training, live data otherwise
  const displayTrainingSummary = isTrainingActiveState ? (frozenContextRef.current?.trainingSummary || trainingSummary) : trainingSummary;
  const displayExecutionHistory = isTrainingActiveState ? (frozenContextRef.current?.executionHistory || executionHistory) : executionHistory;
  
  // Training completion detection with stable state
  const currentProgress = trainingProgress?.progress?.percentage || 0;
  const isTrainingCompleted = useMemo(() => {
    return currentProgress >= 100 || trainingStatus?.status === 'completed';
  }, [currentProgress, trainingStatus?.status]);
  


  // Helper functions - moved before memoized components to fix initialization error
  const getModelName = (modelType) => {
    switch(modelType) {
      // Tabular models
      case 'logreg': return 'Logistic Regression';
      case 'mlp': return 'Multi-Layer Perceptron';
      case 'tabnet': return 'TabNet';
      case 'fttransformer':
      case 'ft_transformer': return 'FT-Transformer';
      // Legacy tabular models
      case 'cnn': return 'Convolutional Neural Network';
      case 'lstm': return 'LSTM';
      case 'transformer': return 'Transformer';
      // Imaging models
      case 'convnext': return 'ConvNeXt';
      case 'resnet18': return 'ResNet18';
      case 'swintransformer': return 'Swin Transformer';
      default: return modelType || 'Unknown';
    }
  };

  const getDatasetName = (datasetPath) => {
    if (!datasetPath) return 'Unknown';
    return datasetPath.split('/').pop() || datasetPath;
  };

  // Whether an executionHistory entry came from a segmentation run. Used by
  // the Latest Training Results card to swap "Accuracy" → "Dice" so seg
  // runs don't render a meaningless 0% (backend leaves final_accuracy NULL
  // for seg; AppContext mapper falls it back to 0; old UI showed 0%).
  const isSegRun = (exec) => {
    const pt = exec?.config_data?.dataset_config?.processing_type;
    if (pt === 'imaging_segmentation') return true;
    // Fallback when config_data is missing on the run record — model_type
    // heuristic. Keep in sync with backend seg model registry.
    const mt = String(exec?.modelType || '').toLowerCase();
    return /(^|[_-])(unet|unet3d|deeplab)([_-]|$)/.test(mt);
  };

  // Run-level primary metric — accuracy for tabular/cls, dice for seg.
  // Returns undefined when missing so callers can render 'N/A' instead of
  // misleading 0%. (Backend may surface dice as final_dice_hard, final_dice,
  // or dice_hard depending on release; check all three.)
  const finalPrimaryMetric = (exec) => {
    if (isSegRun(exec)) {
      return exec?.final_dice_hard ?? exec?.final_dice ?? exec?.dice_hard;
    }
    return exec?.accuracy;
  };

  // Per-round primary metric for the chart series. Returns null when missing
  // so Chart.js skips the point cleanly (no flat-0 line).
  const perRoundPrimaryMetric = (exec, round) => {
    if (isSegRun(exec)) {
      return round?.dice_hard ?? round?.dice ?? null;
    }
    return round?.accuracy ?? null;
  };

  // Refresh training data periodically - but only when training is not active
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh data when training is not active to prevent flickering
      if (fetchTrainingSummary && !isTrainingActiveState) {
        fetchTrainingSummary(true);
      } else {
      }
    }, 30000); // Refresh every 30 seconds

    return () => {
      clearInterval(interval);
    };
  }, [fetchTrainingSummary, isTrainingActiveState]); // Depend on training status to control refreshing

  
  const handleRefreshData = async () => {
    if (fetchTrainingSummary) {
      setTrainingDataLoading(true);
      try {
        await fetchTrainingSummary();
      } finally {
        setTrainingDataLoading(false);
      }
    }
  };

  const handleUseConfiguration = async (arg) => {
    // Determine if arg is activity object or runId string
    console.log('arg', arg);
    const runId = (arg && typeof arg === 'object') ? arg.id : arg;
    console.log('[Dashboard] handleUseConfiguration processing:', runId);

    try {
      if (!runId) return;

      // If we received the full activity object, just use it
      if (arg && typeof arg === 'object' && arg.executionData) {
          await handleActivityClick(arg);
          return;
      }
      
      // otherwise look it up
      const run = executionHistory.find(r => r.id === runId || r.trainingRunId === runId);
      if (!run) {
          console.warn('[Dashboard] Config run not found:', runId);
          return;
      }

      // Mimic the activity object expected by handleActivityClick
      const activity = {
          type: 'execution',
          id: run.id,
          executionData: run
      };
      
      await handleActivityClick(activity); 
    } catch (error) {
      console.error('[Dashboard] Error in handleUseConfiguration:', error);
    }
  };

  const handleViewDetails = (activity) => {
      // Use trainingRunId if available, otherwise fallback to activity.id
      // This ensures the modal opens even for legacy runs without explicit DB IDs
      const runId = activity?.executionData?.trainingRunId || activity?.id;

      if (runId) {
          console.log('[Dashboard] Opening details for run:', runId);
          setSelectedRunId(runId);
          // Always pass the full activity object so the modal can render details
          // even when the lookup list doesn't contain a matching entry (mock data,
          // missing trainingRunId, etc.). The modal will prefer this over lookup.
          setInitialSelectedActivity(activity || null);
          setShowAllCompletedRuns(true);
      } else {
          console.error('[Dashboard] Cannot view details: No run ID found', activity);
      }
  };

  const handleClearHistoryConfirm = async () => {
    if (activeContext.clearTrainingRunHistory) {
      await activeContext.clearTrainingRunHistory();
    }
    setOpenClearHistoryModal(false);
  };

  // Check if any data is still loading - stabilize during training to prevent flickering
  const loading = useMemo(() => {
    // During training, never show loading state to prevent flicker
    if (isTrainingActiveState) {
      return false;
    }
    
    // Include executionHistoryLoading as it affects the "Rounds" stats
    const isLoading = modelsLoading || datasetsLoading || statusLoading || configLoading || executionHistoryLoading;

    return isLoading;
  }, [modelsLoading, datasetsLoading, statusLoading, configLoading, executionHistoryLoading, isTrainingActiveState]);
  
  // Memoize stats to prevent re-computation on every render - use frozen data during training
  const stats = useMemo(() => {
    // `Available Datasets` counts LOGICAL datasets the way /datasets renders
    // them (one card per dataset_ref, grouped across participating sites).
    // Falls back to the per-capability count if availableDatasets is empty.
    const logicalDatasets = groupCapabilitiesByDatasetRef(availableDatasets, sites);
    const activeDatasets = logicalDatasets.length || (datasets?.length || 0);

    // `Available Models` matches the canonical model list on /models:
    //   4 Tabular + 3 Imaging · Classification + 3 Imaging · Segmentation = 10.
    // Sourced from the shared catalog so this can never drift from the banner
    // or the /models page again.
    const availableModels = MODEL_COUNT;
    const totalRuns = displayTrainingSummary?.total_runs || 0;
    const completedRuns = displayTrainingSummary?.completed_runs || 0;

    return [
      {
        title: t('dashboard.stats.availableDatasets'),
        value: activeDatasets.toString(),
        // No meaningful prior-period baseline — hide delta pill
        change: null,
        icon: <DataHubDatasetsIcon />,
        color: "#1B365D",
        accent: "#1B365D",
        bg: "var(--fx-paper)",
      },
      {
        title: t('dashboard.stats.availableModels'),
        value: availableModels.toString(),
        // No meaningful prior-period baseline — hide delta pill
        change: null,
        icon: <LearningModelsIcon />,
        color: "#0087B4",
        accent: "#0087B4",
        bg: "var(--fx-paper)",
      },
      {
        title: "Incomplete Rounds",
        value: (totalRuns - completedRuns).toString(),
        // Only show a delta when there is actual run history to compare against
        change: totalRuns > 0 ? `${(((totalRuns - completedRuns) / totalRuns) * 100).toFixed(0)}%` : null,
        icon: <IncompleteRoundsIcon />,
        color: "#d97706",
        accent: "#d97706",
        bg: "var(--fx-paper)",
      },
      {
        title: "Completed Rounds",
        value: completedRuns.toString(),
        // Only show a delta when there is actual run history to compare against
        change: totalRuns > 0 ? `${((completedRuns / totalRuns) * 100).toFixed(0)}%` : null,
        icon: <CompletedRoundsIcon />,
        color: "#16a34a",
        accent: "#16a34a",
        bg: "var(--fx-paper)",
      },
    ];
  }, [datasets.length, models.length, availableDatasets, sites, displayTrainingSummary, isTrainingActive, t, colors]);

  // Memoize recent activities to prevent re-computation on every render
  const getAllCompletedRuns = () => {
    const allRuns = [...executionHistory];
    
    // Map historical runs to modal-compatible format
    const mappedHistory = allRuns.map((execution, index) => {
        // If it already has executionData (e.g. from our manual push above, roughly), skip
        // But executionHistory items are raw.
        return {
            id: execution.trainingRunId || execution.id || `run-${execution.timestamp || index}`,
            type: "execution",
            title: 'Training Run',
            description: `Model: ${getModelName(execution.modelType)}, Dataset: ${resolveDatasetDisplayNameForExecution(execution, { siteCapabilities, availableDatasets })}`,
            // Use timestamp if available
            time: formatDateToLocal(execution.timestamp),
            status: execution.status || (execution.completed ? 'completed' : 'incomplete'),
            executionData: execution
        };
    });

    // Check if there's an active run that's not already in history
    if (trainingStatus && (trainingStatus.status === 'running' || trainingStatus.status === 'paused')) {
        const activeRunId = trainingStatus.trainingRunId || 'current-active-run';
        
        // Avoid duplicates if the active run is somehow already in history
        const exists = mappedHistory.some(run => run.id === activeRunId || run.executionData.trainingRunId === activeRunId);
        
        if (!exists) {
            mappedHistory.unshift({
                id: activeRunId,
                title: `Training Run ${activeRunId}`,
                description: `Model: ${currentConfig?.dataset_config?.model_type || 'Unknown'} | Dataset: ${getDatasetName(currentConfig?.dataset_config?.dataset_path)}`,
                time: "In Progress",
                status: trainingStatus.status,
                type: 'execution',
                executionData: {
                    modelType: currentConfig?.dataset_config?.model_type || 'Unknown',
                    datasetPath: currentConfig?.dataset_config?.dataset_path || 'Unknown',
                    rounds: currentConfig?.app_config?.["num-server-rounds"] || 0,
                    completed: false,
                    currentRound: trainingProgress?.progress?.current_round || 0,
                    // Active run metrics
                    accuracy: trainingProgress?.latest_metrics?.round_metrics?.accuracy || 0,
                    loss: trainingProgress?.latest_metrics?.round_metrics?.loss || 0,
                    trainingRunId: activeRunId
                }
            });
        }
    }
    
    return mappedHistory;
  };

  const recentActivities = useMemo(() => {
    const activities = [];
    
    // Only show execution history items - use frozen data during training
    if (displayExecutionHistory && displayExecutionHistory.length > 0) {
      // Sort by timestamp (newest first), filter completed
      const completedExecutions = [...displayExecutionHistory]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .filter(execution => execution.completed);
      
      console.log('[DASHBOARD_DEBUG] First execution raw timestamp:', completedExecutions[0]?.timestamp);
      
      // Return all activities for the modal
      if (showAllActivities) {
        return completedExecutions.map((execution, index) => ({
          id: execution.trainingRunId || execution.id || `run-${execution.timestamp || index}`,
          type: "execution",
          title: t('dashboard.trainingCompleted'),
          description: `${t('dashboard.modelLabel')}: ${getModelName(execution.modelType)}, ${t('dashboard.datasetLabel')}: ${resolveDatasetDisplayNameForExecution(execution, { siteCapabilities, availableDatasets })}`,
          time: formatDateToLocal(execution.timestamp),
          status: "completed",
          icon: <TrendingUpIcon />,
          executionData: execution 
        }));
      }
      
      // Return only first 3 activities for the dashboard
      return completedExecutions.slice(0, 3).map((execution, index) => {
        const formattedTime = formatDateToLocal(execution.timestamp);
        return {
        id: execution.trainingRunId || execution.id || `run-${execution.timestamp || index}`,
        type: "execution",
        title: 'Training Completed',
        description: `Model: ${getModelName(execution.modelType)}, Dataset: ${resolveDatasetDisplayNameForExecution(execution, { siteCapabilities, availableDatasets })}`,
        time: formattedTime,
        status: "completed",
        icon: <TrainingCompletedIcon />,
        executionData: execution 
      };
      });
    }
    
    // No real activities yet — return empty so the UI shows an honest empty state.
    return [];
  }, [displayExecutionHistory, showAllActivities, t]);

  // Activity Calendar Data Aggregation
  const calendarData = useMemo(() => {
    const history = displayExecutionHistory || [];
    const dateMap = {};

    // Helper to format date as YYYY-MM-DD in LOCAL time
    const toLocalDateString = (dateInput) => {
        return formatDateToLocal(dateInput, 'yyyy-MM-dd');
    };

    // Create a map for the last 6 months + remaining days of current month
    const today = new Date();
    // Set end date to the last day of the current month to ensure the label appears
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    // Set start date to 210 days ago
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 210);

    // Initialize all dates in range with 0
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = toLocalDateString(d);
        dateMap[dateStr] = 0;
    }

    // Populate with actual data
    history.forEach(run => {
      if (!run.timestamp) return;
      const dateStr = toLocalDateString(run.timestamp);
      if (dateMap.hasOwnProperty(dateStr)) {
        dateMap[dateStr]++;
      }
    });

    const data = Object.keys(dateMap).map(date => {
      const count = dateMap[date];
      let level = 0;
      if (count >= 10) level = 4;
      else if (count >= 6) level = 3;
      else if (count >= 3) level = 2;
      else if (count >= 1) level = 1;
      return { date, count, level };
    });

    return data.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [displayExecutionHistory]);

  // Activity Stats Calculation
  const activityStats = useMemo(() => {
    const history = displayExecutionHistory || [];

    const totalRuns = history.length;
    let completedRuns = 0;
    const dateMap = {};

    history.forEach(run => {
      if (run.status === 'finished' || run.status === 'completed') {
        completedRuns++;
      }
      if (!run.timestamp) return;
      const date = formatDateToLocal(run.timestamp, 'yyyy-MM-dd');
      if (!dateMap[date]) dateMap[date] = 0;
      dateMap[date]++;
    });

    const incompleteRuns = totalRuns - completedRuns;
    const topDays = Object.keys(dateMap)
      .map(date => ({
        date,
        count: dateMap[date],
        dayOfWeek: formatDateToLocal(date, 'EEE'),
        formattedDate: formatDateToLocal(date, 'MMM d')
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    return { totalRuns, topDays, completedRuns, incompleteRuns };
  }, [displayExecutionHistory]);

  const calendarTheme = {
    light: [
      colors.grey[200],       // level 0
      "#d4e8f0",              // level 1 - Very light brand blue
      "#8bbdd4",              // level 2 - Light brand blue
      "#0087B4",              // level 3 - Brand secondary
      "#1B365D"               // level 4 - Brand primary navy
    ],
    dark: [
      colors.grey[800],       // level 0
      "#d4e8f0",              // level 1 - Very light brand blue
      "#8bbdd4",              // level 2 - Light brand blue
      "#0087B4",              // level 3 - Brand secondary
      "#1B365D"               // level 4 - Brand primary navy
    ]
  };





  const handleActivityClick = async (activity) => {
    if (activity.type === 'execution' && activity.executionData) {
      console.log('[Dashboard] Loading configuration for activity:', activity.id);
      try {

        
        // dev-0.7.51 — Build the restored config via the shared helper so the
        // Dashboard and Execute "Use This Configuration" buttons behave
        // identically. When the run carries a stored config_data blob it is
        // passed through wholesale (every datapoint restored verbatim,
        // including sites[], selected_datasets, feature_filters and imaging/
        // segmentation hyperparameters). A flat-column reconstruction is used
        // only for legacy runs that predate config_data.
        const configData = await buildConfigDataFromActivity(activity);
        if (!configData) {
          console.warn('[Dashboard] Could not build config from activity', activity);
          return;
        }
        console.log('[Dashboard] Restored configData:', configData);

        // Save the configuration to the backend like the configs component does
        if (activeContext.saveConfig) {
            console.log("[Dashboard] Saving config to context:", configData);
            await activeContext.saveConfig(configData);
        } else {
            console.warn('[Dashboard] saveConfig function missing from context');
        }

        // Clear previous training data to ensure fresh start
        if (activeContext.clearAllTrainingData) {
            await activeContext.clearAllTrainingData();
        }
        
        // Also set it in the context for immediate use
        if (activeContext.setCurrentConfig) {
            activeContext.setCurrentConfig(configData);
        }
        
        // Navigate to configs page with pre-populated values.
        // restored:true tells the Configs page to honour this past run's
        // hyperparameters instead of overriding them with the Settings defaults.
        console.log('[Dashboard] Navigating to /configs with config');
        navigate('/configs', { state: { restored: true } });
        
      } catch (error) {
        console.error('Error loading configuration:', error);
        // Fallback: still navigate but without saved config
        navigate('/execute');
      }
    } else {
        console.warn('[Dashboard] Cannot load config: Invalid activity type or missing data', activity);
    }
  };

  // if (loading) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
  //       <CircularProgress sx={{ color: colors.secondary[500] }} />
  //       <Typography variant="body1" sx={{ color: colors.grey[300], ml: 2 }}>
  //         Loading dashboard data...
  //       </Typography>
  //     </Box>
  //   );
  // }

  // Use the memoized recentActivities instead of calling getRecentActivity()
  const hasMoreActivities = displayExecutionHistory?.filter(e => e.completed).length > 3;

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: 'auto' }}>
      {/* Header */}
      <HeaderWithControls
        icon={<DashboardOutlinedIcon />}
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        customControls={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={trainingDataLoading ? <CircularProgress size={14} sx={{ color: '#ffffff' }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={handleRefreshData}
              disabled={trainingDataLoading}
            >
              {trainingDataLoading ? 'Refreshing…' : 'Refresh Data'}
            </Button>
          </Box>
        }
      />

      {/* Hero banner — live federation summary (sites · datasets · models) */}
      {(() => {
        const safeSites = Array.isArray(sites) ? sites : [];
        const sitesOnline = safeSites.filter((s) => s?.status === 'online').length;
        const datasetCount = groupCapabilitiesByDatasetRef(availableDatasets, sites).length
          || (Array.isArray(datasets) ? datasets.length : 0);
        return (
          <DashboardBanner
            sitesOnline={sitesOnline}
            sitesTotal={safeSites.length}
            datasetCount={datasetCount}
            modelCount={MODEL_COUNT}
            sites={safeSites}
            capabilitiesBySite={siteCapabilities || {}}
          />
        );
      })()}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => {
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  background: "var(--surface-card)",
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                  border: "1px solid var(--border-hairline)",
                  boxShadow: "var(--fx-shadow-sm)",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": {
                    borderColor: "var(--border-strong)",
                    boxShadow: "var(--fx-shadow-lg)",
                  },
                }}
              >
                {/* Decorative SVG accent — subtle curve in the corner */}
                <Box
                  component="svg"
                  viewBox="0 0 120 120"
                  sx={{
                    position: "absolute",
                    top: -10,
                    right: -10,
                    width: 90,
                    height: 90,
                    opacity: 0.06,
                    color: stat.accent,
                    pointerEvents: "none",
                  }}
                  aria-hidden="true"
                >
                  <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="60" cy="60" r="32" fill="none" stroke="currentColor" strokeWidth="1" />
                  <circle cx="60" cy="60" r="14" fill="currentColor" />
                </Box>
                <CardContent sx={{ p: 2 }}>
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1 }} animation="wave" />
                        <Box>
                          <Skeleton variant="text" width={40} height={26} animation="wave" />
                          <Skeleton variant="text" width={110} height={14} animation="wave" />
                        </Box>
                      </Box>
                      <Skeleton variant="rounded" width={44} height={20} sx={{ borderRadius: 1 }} animation="wave" />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.25,
                            backgroundColor: `${stat.accent}14`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: stat.accent,
                            "& svg": { fontSize: 18 },
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Box>
                          <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: "1.375rem", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
                            {stat.value}
                          </Typography>
                          <Typography sx={{ color: colors.grey[500], fontWeight: 500, fontSize: "0.75rem", mt: 0.25 }}>
                            {stat.title}
                          </Typography>
                        </Box>
                      </Box>
                      {stat.change !== null && (
                        <Chip
                          label={stat.change}
                          size="small"
                          sx={{
                            backgroundColor: `${stat.accent}14`,
                            color: stat.accent,
                            border: 'none',
                            fontWeight: 600,
                            fontSize: "0.6875rem",
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ────────────────────────────────────────────────────────────────
          Federation health + Quick actions + Pipeline snapshot
          Three compact panels that turn the Overview into a true mission
          control for data scientists. All values are derived from the
          existing AppContext (sites, currentConfig, models, datasets) —
          no new backend calls.
          ──────────────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* ─── Federation Health ───────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          {(() => {
            const safeSites = Array.isArray(sites) ? sites : [];
            const online = safeSites.filter((s) => s?.status === 'online').length;
            const offline = safeSites.filter((s) => s?.status === 'offline').length;
            const maint = safeSites.filter((s) => s?.status === 'maintenance').length;
            const total = safeSites.length;
            const health = total > 0 ? Math.round((online / total) * 100) : 0;
            const healthColor = health >= 80 ? '#16a34a' : health >= 50 ? '#d97706' : '#dc2626';

            // Site type breakdown — hospitals vs research institutions
            const isHospital = (s) => {
              const name = (s?.display_name || '').toLowerCase();
              const org = (s?.organization_display_name || '').toLowerCase();
              return name.includes('hospital') || org.includes('health') || name.includes('centre') && !name.includes('research') && !name.includes('cancer research');
            };
            const hospitals = safeSites.filter((s) => isHospital(s));
            const research = safeSites.filter((s) => !isHospital(s));
            const hospitalsOnline = hospitals.filter((s) => s?.status === 'online').length;
            const researchOnline = research.filter((s) => s?.status === 'online').length;

            // Sync freshness: derive the most-recent last_seen across all sites,
            // then format with formatRelativeTime so stale stamps roll up to
            // days/weeks (avoids raw counts like "595h ago").
            const lastSeenTimes = safeSites
              .map((s) => (s?.last_seen ? new Date(s.last_seen).getTime() : null))
              .filter((t) => typeof t === 'number' && !isNaN(t));
            const mostRecent = lastSeenTimes.length ? Math.max(...lastSeenTimes) : null;
            const latencyLabel = mostRecent == null ? 'N/A' : (formatRelativeTime(mostRecent) || 'N/A');

            return (
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountTreeOutlinedIcon sx={{ color: '#0087B4', fontSize: 18 }} />
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '-0.005em' }}>
                        Federation health
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${health}%`}
                      sx={{
                        bgcolor: `${healthColor}14`,
                        color: healthColor,
                        border: 'none',
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        height: 20,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
                    <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1, letterSpacing: '-0.02em', fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                      {online}
                    </Typography>
                    <Typography sx={{ color: colors.grey[500], fontSize: '0.75rem' }}>
                      of {total} sites online
                    </Typography>
                  </Box>

                  {/* health bar */}
                  <Box sx={{ position: 'relative', height: 6, borderRadius: 999, bgcolor: 'var(--fx-border)', overflow: 'hidden', mb: 1.25 }}>
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, bottom: 0,
                      width: `${health}%`,
                      bgcolor: healthColor,
                      transition: 'width 0.4s ease',
                    }} />
                  </Box>

                  {/* status dots */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
                    {[
                      { dot: '#16a34a', label: 'Online', count: online },
                      { dot: '#d97706', label: 'Maintenance', count: maint },
                      { dot: 'var(--fx-text-muted)', label: 'Offline', count: offline },
                    ].map((s) => (
                      <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircleIcon sx={{ fontSize: 7, color: s.dot }} />
                        <Typography sx={{ color: colors.grey[500], fontSize: '0.7rem', fontWeight: 500 }}>
                          {s.label}
                          <Box component="span" sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, ml: 0.5 }}>
                            {s.count}
                          </Box>
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* site composition rows */}
                  <Box sx={{ borderTop: '1px dashed var(--fx-border)', pt: 1, mb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {[
                      { label: 'Hospitals', online: hospitalsOnline, total: hospitals.length, color: '#0087B4' },
                      { label: 'Research', online: researchOnline, total: research.length, color: '#4338ca' },
                    ].map((row) => {
                      const pct = row.total > 0 ? (row.online / row.total) * 100 : 0;
                      return (
                        <Box key={row.label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                            <Typography sx={{ color: colors.grey[500], fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {row.label}
                            </Typography>
                            <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.7rem', fontWeight: 700 }}>
                              {row.online}<Box component="span" sx={{ color: colors.grey[500], fontWeight: 500 }}>/{row.total}</Box>
                            </Typography>
                          </Box>
                          <Box sx={{ position: 'relative', height: 4, borderRadius: 999, bgcolor: 'var(--fx-subtle)', overflow: 'hidden' }}>
                            <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: row.color, opacity: 0.85 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* footer — sync freshness */}
                  <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--fx-subtle)', pt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a', boxShadow: '0 0 0 3px rgba(22,163,74,0.15)' }} />
                      <Typography sx={{ color: colors.grey[500], fontSize: '0.7rem', fontWeight: 500 }}>
                        Last sync
                      </Typography>
                    </Box>
                    <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.7rem', fontWeight: 700, fontFamily: 'var(--fx-font-mono)' }}>
                      {latencyLabel}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })()}
        </Grid>

        {/* ─── DataHub breakdown (datasets by task family) ──────────── */}
        {(() => {
          const all = groupCapabilitiesByDatasetRef(availableDatasets, sites);
          const counts = { all: all.length, tabular: 0, imaging: 0, segmentation: 0 };
          for (const g of all) {
            if (g.dataset_type === 'tabular') counts.tabular++;
            if (g.dataset_type === 'imaging') {
              if (g.tasks_supported?.includes('imaging_classification')) counts.imaging++;
              if (g.tasks_supported?.includes('imaging_segmentation')) counts.segmentation++;
            }
          }
          const rows = [
            { label: 'Tabular',                  value: counts.tabular,      color: '#6366f1' },
            { label: 'Imaging · Classification', value: counts.imaging,      color: '#10b981' },
            { label: 'Imaging · Segmentation',   value: counts.segmentation, color: '#14b8a6' },
          ];
          const maxVal = Math.max(1, ...rows.map((r) => r.value));
          return (
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DatasetOutlinedIcon sx={{ color: '#0087B4', fontSize: 18 }} />
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '-0.005em' }}>
                        DataHub
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${counts.all} total`}
                      sx={{ bgcolor: 'rgba(0,135,180,0.12)', color: '#0087B4', border: 'none', fontWeight: 700, fontSize: '0.6875rem', height: 20 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, justifyContent: 'center' }}>
                    {rows.map((r) => (
                      <Box
                        key={r.label}
                        onClick={() => navigate('/datasets')}
                        sx={{ cursor: 'pointer', '&:hover .fx-dh-label': { color: 'var(--fx-text-strong)' } }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: r.color, flexShrink: 0 }} />
                            <Typography className="fx-dh-label" sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.15s ease' }}>
                              {r.label}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: 'var(--fx-text-strong)', fontWeight: 700, fontSize: '0.8125rem', fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, pl: 1 }}>
                            {r.value}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'var(--fx-subtle)', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', borderRadius: 999, width: `${Math.round((r.value / maxVal) * 100)}%`, minWidth: r.value > 0 ? 6 : 0, bgcolor: r.color, transition: 'width 0.3s ease' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* ─── Pipeline Snapshot ───────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          {(() => {
            const cfg = currentConfig || {};
            const ds = cfg.dataset_config || {};
            const app = cfg.app_config || {};
            const ptype = ds.processing_type;
            const ptypeLabel = ptype === 'imaging_segmentation' ? 'Imaging / Segmentation'
              : ptype === 'imaging_classification' ? 'Imaging / Classification'
              : ptype === 'tabular' ? 'Tabular'
              : 'N/A';
            const ptypeColor = ptype === 'imaging_segmentation' ? '#0f766e'
              : ptype === 'imaging_classification' ? '#047857'
              : ptype === 'tabular' ? '#4338ca'
              : colors.grey[500];
            const selectedSitesCount = Array.isArray(cfg.sites) ? cfg.sites.length : 0;
            const hasConfig = !!cfg && (ds.model_type || ds.processing_type);

            return (
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InsightsOutlinedIcon sx={{ color: '#0087B4', fontSize: 18 }} />
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '-0.005em' }}>
                        Pipeline snapshot
                      </Typography>
                    </Box>
                    {hasConfig ? (
                      <Chip
                        size="small"
                        label="Saved"
                        sx={{ bgcolor: 'rgba(22,163,74,0.10)', color: '#16a34a', border: 'none', fontWeight: 700, fontSize: '0.6875rem', height: 20 }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Not set"
                        sx={{ bgcolor: 'var(--fx-subtle)', color: 'var(--fx-text-secondary)', border: 'none', fontWeight: 700, fontSize: '0.6875rem', height: 20 }}
                      />
                    )}
                  </Box>

                  {hasConfig ? (
                    <Stack divider={<Box sx={{ borderTop: '1px dashed var(--fx-border)' }} />}>
                      {[
                        ['Task',     <Box key="task" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: ptypeColor, fontWeight: 700, fontSize: '0.75rem' }}>{ptypeLabel}</Box>],
                        ['Model',    ds.model_type || 'N/A'],
                        ['Rounds',   app['num-server-rounds'] ?? 'N/A'],
                        ['Epochs',   app['local-epochs'] ?? 'N/A'],
                        ['Sites',    selectedSitesCount > 0 ? `${selectedSitesCount} selected` : 'N/A'],
                      ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.6 }}>
                          <Typography sx={{ color: colors.grey[500], fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {k}
                          </Typography>
                          {typeof v === 'string' || typeof v === 'number' ? (
                            <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.75rem', fontWeight: 600, fontFamily: k === 'Model' ? 'var(--fx-font-mono)' : 'inherit' }}>
                              {v}
                            </Typography>
                          ) : (
                            v
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography sx={{ color: colors.grey[500], fontSize: '0.75rem', mb: 1.25 }}>
                        No active configuration saved.
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<TuneOutlinedIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate('/configs')}
                      >
                        Configure now
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </Grid>
      </Grid>

      {/* Recent Activity + Federation Map — 50/50 bottom section */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          {/* Left — Federation map (50%); opens the full interactive map modal */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <SitesMapPanel
              sites={Array.isArray(sites) ? sites : []}
              capabilitiesBySite={siteCapabilities || {}}
            />
          </Grid>

          {/* Right — Recent activities (50%) */}
          <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ModelTrainingIcon sx={{ color: '#0087B4', fontSize: 18 }} />
            <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.005em' }}>
              {t('dashboard.recentActivities')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {displayExecutionHistory && displayExecutionHistory.length > 0 && (
              <>
                <Button
                  id="dashboard-actions-button"
                  aria-controls={openMenu ? 'dashboard-actions-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openMenu ? 'true' : undefined}
                  variant="outlined"
                  color="primary"
                  onClick={handleMenuClick}
                  sx={{
                    minWidth: '36.5px',
                    width: '36.5px',
                    height: '36.5px',
                    p: 0,
                  }}
                >
                  <MoreHorizIcon />
                </Button>
                <Menu
                  id="dashboard-actions-menu"
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'dashboard-actions-button',
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={() => {
                    handleMenuClose();
                    setOpenClearHistoryModal(true);
                  }}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" sx={{ color: '#7c5cbf' }} />
                    </ListItemIcon>
                    <ListItemText primary="Clear History" sx={{ color: '#7c5cbf' }} />
                  </MenuItem>
                </Menu>
              </>
            )}
            {hasMoreActivities && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  setSelectedRunId(null);
                  setInitialSelectedActivity(null);
                  setShowAllCompletedRuns(true);
                }}
              >
                All Executions
              </Button>
            )}
          </Box>
        </Box>
        
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {executionHistoryLoading ? (
                 // Loading skeleton state
                 Array(3).fill(0).map((_, index) => (
                   <Card
                     key={`skeleton-${index}`}
                     sx={{
                       backgroundColor: "var(--fx-paper)",
                       borderRadius: 2,
                       border: "1px solid var(--fx-border)",
                       height: '140px' 
                     }}
                   >
                     <CardContent sx={{ p: 3 }}>
                       <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                         <Skeleton variant="rounded" width={48} height={48} animation="wave" />
                         <Box sx={{ flex: 1 }}>
                           <Skeleton variant="text" width="40%" height={32} sx={{ mb: 0.5 }} animation="wave" />
                           <Skeleton variant="text" width="80%" height={24} animation="wave" />
                           <Skeleton variant="text" width="30%" height={20} sx={{ mt: 1 }} animation="wave" />
                         </Box>
                         <Skeleton variant="rounded" width={80} height={24} animation="wave" />
                       </Box>

                     </CardContent>
                   </Card>
                 ))
              ) : recentActivities.length === 0 ? (
                <Card sx={{ backgroundColor: 'var(--fx-paper)', borderRadius: 2, border: '1px solid var(--fx-border)', boxShadow: 'var(--fx-shadow-sm)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'var(--fx-subtle)', color: colors.grey[500], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <TrendingUpIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.875rem' }}>
                        No recent activity
                      </Typography>
                      <Typography sx={{ color: colors.grey[500], fontSize: '0.78rem' }}>
                        Completed training runs will appear here once you launch your first federated run.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                recentActivities.map((activity) => (
                <Card
                  key={activity.id}
                  sx={{
                    backgroundColor: "var(--fx-paper)",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                    border: "1px solid var(--fx-border)",
                    transition: "all 0.15s ease",
                    boxShadow: "var(--fx-shadow-sm)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      width: 3,
                      backgroundColor: activity.status === 'running' ? colors.secondary[500]
                        : activity.status === 'idle' ? colors.grey[400]
                        : colors.primary[500],
                    },
                    "&:hover": {
                      borderColor: "var(--fx-border-strong)",
                      boxShadow: "var(--fx-shadow-lg)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, pb: activity.type !== 'placeholder' ? '0 !important' : 3 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                      {(() => {
                        const accent = activity.status === 'running' ? colors.secondary[500]
                          : activity.status === 'idle' ? colors.grey[500]
                          : colors.primary[500];
                        return (
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1.25,
                              backgroundColor: `${accent}14`,
                              border: `1px solid ${accent}33`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: accent,
                              flexShrink: 0,
                              "& svg": { fontSize: 20 },
                            }}
                          >
                            {activity.icon}
                          </Box>
                        );
                      })()}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: 'var(--fx-text-primary)', fontWeight: 600 }}>
                          {activity.title}
                          <Box component="span" sx={{ mx: 0.8, color: colors.grey[400], fontWeight: 300 }}>•</Box>
                          <Typography component="span" variant="body2" sx={{ color: colors.grey[500], fontWeight: 400, fontSize: '0.85rem' }}>
                            {activity.time}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                          {activity.description}
                        </Typography>
                      </Box>
                      {
                        activity.status && (
                          <Chip
                        label={activity.status === 'running' ? 'Active' : 
                              activity.status === 'idle' ? 'Idle' : 'Completed'}
                        size="small"
                        sx={{
                          backgroundColor: activity.status === 'running' ? colors.secondary[500] : 
                                          activity.status === 'idle' ? colors.grey[500] : colors.primary[500],
                          color: "#ffffff",
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}
                      />
                        )
                      }
                      
                    </Box>
                    
                    {/* Recent Activities metric strip — 5 columns:
                        ROUNDS · FINAL ACCURACY/DICE · FINAL LOSS · BEST ACCURACY/DICE · DURATION

                        Primary metric is pipeline-aware: seg runs show DICE (so they
                        no longer surface a meaningless 0% accuracy from a NULL
                        backend column). BEST and DURATION columns leverage
                        post-dev-0.7.50 fields (best_accuracy / duration_seconds)
                        — both fall back to "—" cleanly for pre-fix historic rows.

                        Removed (vs. d219e88): the round-1→round-N % delta chips
                        next to the primary and loss values. Mathematically those
                        deltas explode when round-1 starts near zero (e.g. seg dice
                        round 1 = 0.0007 → ~78,000 % chip), and a round-1-to-last-
                        round ratio is not a metric researchers act on. Demo-context
                        also makes single-value labels (FINAL / BEST) self-documenting
                        in a way the bare ACCURACY / LOSS / BEST shorthand wasn't. */}
                    {activity.type === 'execution' && activity.executionData && (() => {
                      const ed = activity.executionData;
                      const isSeg = isSegRun(ed);

                      // Primary metric value + label
                      const primaryVal = finalPrimaryMetric(ed);
                      const primaryLabel = isSeg ? 'FINAL DICE' : 'FINAL ACCURACY';
                      const primaryText = (() => {
                        if (primaryVal == null || isNaN(Number(primaryVal))) return 'N/A';
                        return isSeg
                          ? Number(primaryVal).toFixed(4)
                          : `${(Number(primaryVal) * 100).toFixed(1)}%`;
                      })();

                      // BEST column — pipeline-aware
                      //   seg: max dice across rounds (computed client-side; backend doesn't
                      //        store a best_dice column today)
                      //   tabular / cls: ed.best_accuracy (from enrichment fan-out)
                      const bestLabel = isSeg ? 'BEST DICE' : 'BEST ACCURACY';
                      const bestText = (() => {
                        if (isSeg) {
                          const results = ed.trainingResults || {};
                          const dices = Object.values(results)
                            .map((r) => Number(r?.dice_hard ?? r?.dice))
                            .filter((v) => !isNaN(v));
                          if (dices.length === 0) return '—';
                          return Math.max(...dices).toFixed(4);
                        }
                        if (ed.best_accuracy == null || isNaN(Number(ed.best_accuracy))) return '—';
                        return `${(Number(ed.best_accuracy) * 100).toFixed(1)}%`;
                      })();

                      // DURATION column
                      const durText = (() => {
                        const s = Number(ed.duration);
                        if (!Number.isFinite(s) || s <= 0) return '—';
                        const sec = Math.round(s);
                        const h = Math.floor(sec / 3600);
                        const m = Math.floor((sec % 3600) / 60);
                        const r = sec % 60;
                        if (h > 0) return `${h}h ${m}m`;
                        if (m > 0) return `${m}m ${r}s`;
                        return `${r}s`;
                      })();

                      const Col = ({ label, children, minWidth = '90px' }) => (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth }}>
                          <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 600, display: 'block', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                            {label}
                          </Typography>
                          {children}
                        </Box>
                      );

                      const valueSx = { color: 'var(--fx-text-primary)', fontWeight: 700, lineHeight: 1.2, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' };

                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1.5, mb: 2, py: 1.25, px: 2, bgcolor: 'var(--fx-subtle)', border: '1px solid var(--fx-border)', borderRadius: 1.5, gap: 1 }}>
                          <Col label="ROUNDS" minWidth="60px">
                            <Typography variant="body2" sx={valueSx}>
                              {ed.completed ? `${ed.total_rounds_completed}` : `${ed.currentRound}`}
                            </Typography>
                          </Col>
                          <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                          <Col label={primaryLabel}>
                            <Typography variant="body2" sx={{ ...valueSx, color: '#0087B4' }}>{primaryText}</Typography>
                          </Col>
                          <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                          <Col label="FINAL LOSS">
                            <Typography variant="body2" sx={{ ...valueSx, color: '#7c5cbf' }}>
                              {ed.loss != null ? Number(ed.loss).toFixed(4) : 'N/A'}
                            </Typography>
                          </Col>
                          <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                          <Col label={bestLabel}>
                            <Typography variant="body2" sx={{ ...valueSx, color: '#16a34a' }}>
                              {bestText}
                            </Typography>
                          </Col>
                          <Divider orientation="vertical" flexItem sx={{ opacity: 0.6 }} />

                          <Col label="DURATION" minWidth="70px">
                            <Typography variant="body2" sx={valueSx}>
                              {durText}
                            </Typography>
                          </Col>
                        </Box>
                      );
                    })()}
                    
                  </CardContent>

                    {/* Action Buttons for Activity - Split Bottom Design */ }
                    {activity.type !== 'placeholder' && (
                      <Box sx={{ display: 'flex', width: '100%', borderTop: `1px solid ${colors.grey[800]}` }}>
                        <Button
                          variant="text"
                          startIcon={<PlayArrowIcon sx={{ fontSize: '1.2rem !important' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUseConfiguration(activity);
                          }}
                          sx={{
                            flex: 1,
                            color: colors.grey[600],
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            transition: 'all 0.2s',
                            height: '40px',
                            borderRadius: 0,
                            borderRight: `1px solid ${colors.grey[800]}`,
                            '&:hover': {
                              color: '#ffffff',
                              backgroundColor: colors.secondary[500],
                            }
                          }}
                        >
                          Run Again
                        </Button>
                        <Button
                          variant="text"
                          endIcon={<ArrowForwardIcon sx={{ fontSize: '1.2rem !important' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(activity);
                          }}
                          sx={{
                            flex: 1,
                            color: colors.secondary[600],
                            textTransform: 'none',
                            fontWeight: 700,
                            height: '40px',
                            fontSize: '0.9rem',
                            borderRadius: 0,
                            transition: 'all 0.2s',
                            '&:hover': {
                              color: '#ffffff',
                              backgroundColor: colors.secondary[500],
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    )}
                  </Card>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>





      {/* Activity Calendar — hidden per product decision */}
      {false && (
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ color: 'var(--fx-text-primary)', fontWeight: 600, mb: 4 }}>
          {t('dashboard.activityCalendar') || "Training Activity"}
        </Typography>
        <Card
          sx={{
            background: "var(--fx-paper)",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            border: "1px solid var(--fx-border)",
            p: 4
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' } }}>
            {/* Calendar Section - 50% Width */}
            <Box 
              ref={containerRef} 
              sx={{ 
                flex: { lg: '0 0 50%' },
                maxWidth: { lg: '50%' },
                borderRight: { lg: `1px solid ${colors.grey[700]}` },
                pr: { lg: 4 },
                pb: { xs: 4, lg: 0 },
                borderBottom: { xs: `1px solid ${colors.grey[200]}`, lg: 'none' },
                mb: { xs: 4, lg: 0 },
                overflow: 'hidden'
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="rectangular" width="100%" height={140} animation="wave" sx={{ borderRadius: 1 }}  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                     <Skeleton variant="text" width={40} height={20} animation="wave" />
                     <Skeleton variant="text" width={40} height={20} animation="wave" />
                  </Box>
                </Box>
              ) : (
                  <ActivityCalendar
                    data={calendarData.length > 0 ? calendarData : [{ date: formatDateToLocal(new Date(), 'yyyy-MM-dd'), count: 0, level: 0 }]}
                    theme={calendarTheme}
                    colorScheme="dark"
                    blockSize={blockSize}
                    blockRadius={4}
                    fontSize={Math.max(10, blockSize)}
                    showWeekdayLabels
                    hideLegend
                    renderBlock={(block, activity) => {
                      // Hide future dates visual but keep them in data layout for month label
                      const todayStr = formatDateToLocal(new Date(), 'yyyy-MM-dd');
                      if (activity.date > todayStr) return null;

                      return (
                        <MuiTooltip
                          title={`${activity.count} run${activity.count !== 1 ? 's' : ''} on ${activity.date}`}
                          placement="top"
                          arrow
                        >
                          {block}
                        </MuiTooltip>
                      );
                    }}
                    labels={{
                      months: [
                        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                      ],
                      weekdays: [
                        'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'
                      ],
                      totalCount: `{{count}} runs in ${new Date().getFullYear()}`
                    }}
                  />
              )}
            </Box>


            {/* Stats Sidebar - 50% Width containing List + Donut */}
            <Box sx={{ flex: { lg: '0 0 50%' }, maxWidth: { lg: '50%' }, pl: { lg: 4 }, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
               
               {/* Most Active Days List */}
               <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                  <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.005em', mb: 1.5 }}>
                    Most active days
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {loading ? (
                         // Skeleton Loader for Top Days
                         [1, 2, 3].map((i) => (
                           <Skeleton 
                             key={i} 
                             variant="rectangular" 
                             height={40} 
                             sx={{ borderRadius: 10 }} 
                             animation="wave" 
                           />
                         ))
                      ) : (
                        activityStats.topDays.length > 0 ? (
                            activityStats.topDays.map((stat, index) => (
                              <Box key={stat.date} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--fx-subtle)', border: '1px solid var(--fx-border)', borderRadius: 1.5, py: 1, px: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0 }}>
                                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, fontFamily: 'var(--fx-font-mono)', bgcolor: index === 0 ? '#0087B4' : 'var(--fx-border)', color: index === 0 ? 'var(--fx-paper)' : 'var(--fx-text-secondary)' }}>
                                       {index + 1}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--fx-text-primary)', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                         {stat.formattedDate}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'var(--fx-text-muted)' }}>
                                        {stat.dayOfWeek}
                                      </Typography>
                                    </Box>
                                </Box>
                                <Chip
                                  label={`${stat.count} ${stat.count === 1 ? 'run' : 'runs'}`}
                                  size="small"
                                  sx={{
                                    flexShrink: 0,
                                    bgcolor: index === 0 ? 'rgba(0,135,180,0.10)' : 'var(--fx-subtle)',
                                    color: index === 0 ? '#0087B4' : 'var(--fx-text-secondary)',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    fontFamily: 'var(--fx-font-mono)',
                                  }}
                                />
                              </Box>
                            ))
                        ) : (
                          <Typography variant="body2" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                            No activity recorded yet
                          </Typography>
                        )
                      )}
                  </Box>
               </Box>

               {/* Donut Chart Section */}
               <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 300 }}>
                  <Box sx={{ position: 'relative', width: 150, height: 150 }}>
                    {loading ? (
                         <Skeleton 
                           variant="circular" 
                           width={150} 
                           height={150} 
                           animation="wave"
                          
                         />
                    ) : (
                    activityStats.totalRuns === 0 ? (
                      <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 1
                      }}>
                        <PieChartIcon sx={{ fontSize: 48, color: colors.grey[700], opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                          No data available
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Doughnut 
                          data={{
                            labels: ['Completed', 'Incomplete'],
                            datasets: [
                              {
                                data: [activityStats.completedRuns, activityStats.incompleteRuns],
                                backgroundColor: ['#16a34a', '#d97706'],
                                borderWidth: 2,
                                cutout: '75%',
                              
                              },
                            ],
                          }}
                          options={{
                            plugins: {
                              tooltip: { 
                                enabled: true,
                                callbacks: {
                                  label: function(context) {
                                    return ` ${context.label}: ${context.raw}`;
                                  }
                                }
                              },
                              legend: { display: false }
                            },
                            maintainAspectRatio: false,
                            responsive: true
                          }}
                        />
                        {/* Centered Total */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none'
                          }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.grey[100], fontFamily: 'var(--fx-font-mono)', lineHeight: 1 }}>
                              {activityStats.totalRuns}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--fx-text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.58rem', mt: 0.25 }}>
                              Total runs
                            </Typography>
                        </Box>
                        </>
                    ))}
                  </Box>

                  {/* Legend Labels */}
                  {activityStats.totalRuns > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 3, gap: 3 }}>
                    {loading ? (
                        <>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Skeleton variant="circular" width={12} height={12} animation="wave" />
                               <Skeleton variant="text" width={60} height={24} animation="wave" />
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Skeleton variant="circular" width={12} height={12} animation="wave" />
                               <Skeleton variant="text" width={60} height={24} animation="wave" />
                           </Box>
                        </>
                    ) : (
                        <>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#16a34a' }} />
                               <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 600 }}>
                                  Completed
                               </Typography>
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#d97706' }} />
                               <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 600 }}>
                                  Incomplete
                               </Typography>
                           </Box>
                        </>
                    )}
                  </Box>
                  )}
               </Box>
            </Box>

          </Box>
        </Card>
      </Box>
      )}


      {/* Model Performance Insights — hidden per product decision */}
      {false && (
      <Box sx={{ mt: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" sx={{ color: 'var(--fx-text-primary)', fontWeight: 600 }}>
            Model Performance Insights
          </Typography>
          <Typography sx={{ color: colors.grey[500], fontSize: '0.75rem', fontWeight: 500 }}>
            Derived from completed runs
          </Typography>
        </Box>

        {(() => {
          // Build leaderboard from completed runs (real or mock fallback)
          const history = (displayExecutionHistory && displayExecutionHistory.length > 0)
            ? displayExecutionHistory.filter((r) => r.completed && typeof r.accuracy === 'number')
            : [];

          // Aggregate by model: best accuracy, run count, avg loss.
          // Normalize to lowercase so "MLP" and "mlp" rows from past runs
          // (case drifted across backend versions) merge into one entry
          // and the leaderboard displays consistently lowercase identifiers.
          const byModel = {};
          history.forEach((r) => {
            const key = (r.modelType || 'unknown').toLowerCase();
            if (!byModel[key]) byModel[key] = { runs: 0, bestAcc: 0, lossSum: 0, lossCount: 0, latest: 0 };
            byModel[key].runs += 1;
            if (r.accuracy > byModel[key].bestAcc) byModel[key].bestAcc = r.accuracy;
            if (typeof r.loss === 'number') {
              byModel[key].lossSum += r.loss;
              byModel[key].lossCount += 1;
            }
            const ts = new Date(r.timestamp || 0).getTime();
            if (ts > byModel[key].latest) byModel[key].latest = ts;
          });
          let leaderboard = Object.entries(byModel).map(([model, m]) => ({
            model,
            runs: m.runs,
            bestAcc: m.bestAcc,
            avgLoss: m.lossCount > 0 ? m.lossSum / m.lossCount : null,
            latest: m.latest,
          }));

          // Sort by best accuracy desc — real completed runs only (no mock fallback)
          leaderboard.sort((a, b) => b.bestAcc - a.bestAcc);
          leaderboard = leaderboard.slice(0, 5);
          const hasRuns = leaderboard.length > 0;

          // Task type distribution from real execution history only.
          // Classification / segmentation pipelines are not wired up yet, so
          // those correctly read 0 until such runs actually exist.
          const taskByType = { tabular: 0, classification: 0, segmentation: 0 };
          history.forEach((r) => {
            const m = (r.modelType || '').toLowerCase();
            if (['convnext', 'resnet18', 'swintransformer'].includes(m)) taskByType.classification += 1;
            else if (m === 'unet') taskByType.segmentation += 1;
            else taskByType.tabular += 1;
          });
          const taskDist = taskByType;
          const taskDistTotal = taskDist.tabular + taskDist.classification + taskDist.segmentation;
          const taskRows = [
            { label: 'Tabular',        value: taskDist.tabular,        color: '#4338ca' },
            { label: 'Classification', value: taskDist.classification, color: '#0087B4' },
            { label: 'Segmentation',   value: taskDist.segmentation,   color: '#0f766e' },
          ];

          // Best run summary
          const bestRun = leaderboard[0];
          const bestRunDate = bestRun?.latest
            ? new Date(bestRun.latest).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : 'N/A';

          return (
            <Grid container spacing={2}>
              {/* Best run highlight */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', color: '#16a34a' }}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l-3 9h18l-3-9-4 4-3-6-3 6-2-4z" />
                          <circle cx="12" cy="4" r="1.5" />
                        </svg>
                      </Box>
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '-0.005em' }}>
                        Best performing model
                      </Typography>
                    </Box>

                    {bestRun ? (
                      <>
                        <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, letterSpacing: '-0.02em', fontFamily: 'var(--fx-font-mono)', mb: 0.25 }}>
                          {bestRun.model}
                        </Typography>
                        <Typography sx={{ color: colors.grey[500], fontSize: '0.75rem', mb: 2 }}>
                          Top run on {bestRunDate} • {bestRun.runs} run{bestRun.runs === 1 ? '' : 's'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ flex: 1, p: 1.25, borderRadius: 1, bgcolor: 'rgba(22,163,74,0.08)' }}>
                            <Typography sx={{ color: '#16a34a', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.25 }}>
                              Best accuracy
                            </Typography>
                            <Typography sx={{ color: '#16a34a', fontSize: '1.25rem', fontWeight: 700, lineHeight: 1, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                              {(bestRun.bestAcc * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, p: 1.25, borderRadius: 1, bgcolor: 'rgba(124,92,191,0.08)' }}>
                            <Typography sx={{ color: '#7c5cbf', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.25 }}>
                              Avg loss
                            </Typography>
                            <Typography sx={{ color: '#7c5cbf', fontSize: '1.25rem', fontWeight: 700, lineHeight: 1, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                              {bestRun.avgLoss != null ? bestRun.avgLoss.toFixed(3) : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ py: 2 }}>
                        <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                          No completed runs yet
                        </Typography>
                        <Typography sx={{ color: colors.grey[500], fontSize: '0.78rem' }}>
                          Launch a training run to see your best-performing model here.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Leaderboard */}
              <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%', border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', color: '#0087B4' }}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="12" width="4" height="9" rx="1" />
                          <rect x="10" y="7" width="4" height="14" rx="1" />
                          <rect x="17" y="3" width="4" height="18" rx="1" />
                        </svg>
                      </Box>
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '-0.005em' }}>
                        Leaderboard
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography sx={{ color: colors.grey[500], fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Best accuracy
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {!hasRuns && (
                        <Typography sx={{ color: colors.grey[500], fontSize: '0.8rem', fontStyle: 'italic', py: 1 }}>
                          No completed runs to rank yet.
                        </Typography>
                      )}
                      {leaderboard.map((row, i) => {
                        const pct = row.bestAcc * 100;
                        const barColor = i === 0 ? '#16a34a' : i === 1 ? '#0087B4' : 'var(--fx-text-muted)';
                        return (
                          <Box key={row.model + i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                              width: 22, height: 22, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: i === 0 ? '#16a34a' : i === 1 ? 'var(--fx-border-strong)' : 'var(--fx-subtle)',
                              color: i === 0 ? 'var(--fx-paper)' : colors.grey[500],
                              fontWeight: 700, fontSize: '0.7rem', flexShrink: 0,
                            }}>
                              {i + 1}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
                                <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'var(--fx-font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.model}
                                </Typography>
                                <Typography sx={{ color: barColor, fontSize: '0.78rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', ml: 1 }}>
                                  {pct.toFixed(1)}%
                                </Typography>
                              </Box>
                              <Box sx={{ position: 'relative', height: 5, borderRadius: 999, bgcolor: 'var(--fx-subtle)', overflow: 'hidden' }}>
                                <Box sx={{
                                  position: 'absolute', inset: 0,
                                  width: `${pct}%`,
                                  background: `linear-gradient(90deg, ${barColor}, ${barColor}cc)`,
                                  transition: 'width 0.4s ease',
                                }} />
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Task distribution */}
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', color: '#4338ca' }}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 3v9l7 5" />
                        </svg>
                      </Box>
                      <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '-0.005em' }}>
                        Task mix
                      </Typography>
                    </Box>

                    {/* Stacked bar */}
                    <Box sx={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', mb: 1.5, bgcolor: 'var(--fx-subtle)' }}>
                      {taskRows.map((r) => {
                        const pct = taskDistTotal > 0 ? (r.value / taskDistTotal) * 100 : 0;
                        if (pct === 0) return null;
                        return (
                          <Box key={r.label} sx={{ width: `${pct}%`, bgcolor: r.color }} />
                        );
                      })}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                      {taskRows.map((r) => {
                        const pct = taskDistTotal > 0 ? (r.value / taskDistTotal) * 100 : 0;
                        return (
                          <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
                              <Typography sx={{ color: colors.grey[500], fontSize: '0.7rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.label}
                              </Typography>
                            </Box>
                            <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.7rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', ml: 1 }}>
                              {r.value} <Box component="span" sx={{ color: colors.grey[500], fontWeight: 500 }}>({pct.toFixed(0)}%)</Box>
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          );
        })()}
      </Box>
      )}



      {/* Recent Activities Section */}
      <ClearHistoryModal
        open={openClearHistoryModal}
        onClose={() => setOpenClearHistoryModal(false)}
        onConfirm={handleClearHistoryConfirm}
      />

      <RecentActivitiesModal
        open={showAllActivities}
        onClose={() => setShowAllActivities(false)}
        activities={recentActivities}
        onActivityClick={handleActivityClick}
      />

      {/* All Completed Runs Modal */}
      <AllCompletedRunsModal
        open={showAllCompletedRuns}
        onClose={() => {
          setShowAllCompletedRuns(false);
          setInitialSelectedActivity(null);
          setSelectedRunId(null);
        }}
        activities={getAllCompletedRuns()}
        onActivityClick={handleActivityClick}
        initialSelectedRunId={selectedRunId}
        initialSelectedActivity={initialSelectedActivity}
      />
    </Box>
  );
};

// Custom comparison function for React.memo to prevent re-renders during training
const arePropsEqual = (prevProps, nextProps) => {
  // Always prevent re-renders - Dashboard should be completely self-contained
  return true;
};

// Wrap with React.memo with custom comparison
export default memo(Dashboard, arePropsEqual);
